import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const newReqId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

// WhatsApp inbox: conversations received on THIS business's own number. Replies only inside Meta's 24h window.
export const WhatsAppInbox = () => {
  const qc = useQueryClient();
  const [sel, setSel] = useState(null);
  const [text, setText] = useState("");
  const rid = useRef(newReqId());
  const { data: convs = [] } = useQuery({ queryKey: ["wa-inbox"], queryFn: async () => (await api.get("/whatsapp/inbox")).data });
  const { data: conv } = useQuery({ queryKey: ["wa-conv", sel], enabled: !!sel, queryFn: async () => (await api.get(`/whatsapp/inbox/${sel}`)).data });
  const send = useMutation({
    mutationFn: () => api.post(`/whatsapp/inbox/${sel}/reply`, { text, request_id: rid.current }),
    onSuccess: (r) => { rid.current = newReqId(); setText(""); qc.invalidateQueries({ queryKey: ["wa-conv", sel] }); toast.message(`Reply: ${r.data.status}`); },
    onError: (e) => toast.error(e?.response?.data?.detail?.msg || apiErr(e)),
  });
  const blocked = conv && (conv.opted_out ? "Customer replied STOP — WhatsApp messages are blocked." : !conv.window_open ? "More than 24 hours since the customer's last message — Meta only allows an approved template." : "");
  return (
    <div className="mt-4 grid gap-3 rounded-xl border border-slate-200 bg-white p-4 md:grid-cols-[260px_1fr]" data-testid="wa-inbox">
      <div className="space-y-1">
        <p className="text-sm font-semibold text-slate-900">WhatsApp inbox</p>
        {!convs.length && <p className="text-xs text-slate-500" data-testid="wa-inbox-empty">No customer messages yet.</p>}
        {convs.map((c) => (
          <button key={c.id} onClick={() => setSel(c.id)} data-testid={`wa-conv-${c.id}`}
            className={`block w-full rounded-lg px-2 py-1.5 text-left text-xs transition-colors ${sel === c.id ? "bg-emerald-50" : "hover:bg-slate-50"}`}>
            <b>{c.customer_name || c.wa_masked}</b>{c.unread > 0 && <span className="ml-1 text-emerald-700">({c.unread})</span>}
            {c.opted_out && <span className="ml-1 text-rose-600">STOP</span>}
            <span className="block truncate text-slate-500">{c.last_text}</span>
          </button>
        ))}
      </div>
      {conv && (
        <div className="flex flex-col gap-2" data-testid="wa-conv-view">
          <div className="max-h-72 space-y-1 overflow-y-auto">
            {conv.messages.map((m, i) => (
              <div key={i} className={`max-w-[80%] rounded-lg px-2 py-1 text-xs ${m.dir === "in" ? "bg-slate-100" : "ml-auto bg-emerald-50"}`}>
                {m.text}<span className="block text-[10px] text-slate-400">{m.status} · {(m.at || "").slice(0, 16).replace("T", " ")}</span>
              </div>
            ))}
          </div>
          {blocked ? <p className="text-xs text-amber-700" data-testid="wa-reply-blocked">{blocked}</p> : (
            <div className="flex gap-2">
              <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Reply…" data-testid="wa-reply-input" />
              <Button size="sm" disabled={!text.trim() || send.isPending} onClick={() => send.mutate()} data-testid="wa-reply-send">Send</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
